import { v4 as uuidv4 } from 'uuid';
import {
  CompositorNodeV3,
  canCopyLayer,
  canDuplicateLayer,
  countOwnedMedia,
  isDeletableLayer,
  resolveLayerResource,
  selectedLayerRoots,
} from '@/types/nodes/compositor';
import { HandleType } from '@/enums/handle-type.enum';
import { WebColor } from '@/UI/ColorPicker/ColorPicker';
import { validateFile } from '@/utils/nodeInputValidation';
import { track } from '@/utils/analytics';
import {
  getFontFamilyKey,
  italicOptionsAtWeight,
  nearestFontStyleCombination,
  styleCombinationsForFamily,
} from '@/utils/customFonts.utils';
import { CompositorAiError, CompositorAiErrorCode } from '@/components/Recipe/CompositorAiTasks/compositorAiError';
import { UndoRedoEntry } from '../undoRedo';
import {
  createFadeProperty,
  editFadeEasing,
  editFadeEnabled,
  editFadedProperty,
  editFadeDurations,
  propertyToFade,
} from '../utils/fades';
import type { FontStyleCombination, WorkspaceFontFamily } from '@/utils/customFonts.utils';
import type { FlowGraph } from '../FlowGraph';
import type { MediaAsset } from '@/types/api/assets';
import type {
  CompositorAiActionKind,
  CompositorAiBounds,
  CompositorAiSubmitResult,
  Alignment,
  BlendMode,
  EffectKind,
  EffectType,
  Easing,
  FileKind,
  Font,
  FontOptions,
  GroupBox,
  HorizontalAlign,
  ImageMode,
  ImageFile,
  Layer,
  LayerId,
  LayerPlacement,
  Property,
  NodeId,
  Orientation,
  ResourceId,
  Stroke,
  StrokeCap,
  StrokeJoin,
  StrokePosition,
  ToolMode,
  VerticalAlign,
} from 'web';

/** Where a newly added layer lands on the stage (engine-defined). Re-exported so
 * the paste, drop, and picker callers share one placement type. */
export type { LayerPlacement } from 'web';

/** How the user brought the content in, for analytics. */
export type AddContentSource = 'upload' | 'workflow-pick' | 'paste' | 'drop';

/** A source snapshot before compositor transforms/effects, including a rendered upstream input. */
export interface CompositorImageSource {
  kind: 'owned' | 'wired_file' | 'wired_computed';
  file: ImageFile;
  /** Resource and wire identity; layout and display names are deliberately excluded. */
  binding?: unknown;
}

/** The stroke a newly added layer stroke gets. */
const DEFAULT_STROKE: Extract<Stroke, { type: 'line' }> = {
  type: 'line',
  color: { r: 0, g: 0, b: 0, a: 1 },
  weight: 2,
  position: 'center',
  join: 'miter',
  cap: 'butt',
  visible: true,
};

/** How long a stroke or fill edit hides the vector edit interface for. */
const VECTOR_EDIT_INTERFACE_HIDE_MS = 1000;

/**
 * The pending re-show of each compositor's vector edit interface, so a run of
 * edits — dragging through colors, scrubbing a weight — keeps it hidden and
 * brings it back once, half a second after the last of them. Held per node
 * rather than per instance because every caller builds its own view over the
 * same node.
 */
const vectorEditInterfaceTimers = new Map<NodeId, ReturnType<typeof setTimeout>>();

/**
 * Wraps the token that a layer copy puts on the clipboard. An HTML comment is
 * used because nothing displays it, so pasting into another program shows
 * nothing at all. Figma marks its own copies the same way, see
 * parseFigmaClipboardHtml.
 */
const CLIPBOARD_TOKEN_START = '<!--(weave-layers)';
const CLIPBOARD_TOKEN_END = '(/weave-layers)-->';

/**
 * A value for a numeric layer property: either the value itself, or a transform run against each
 * layer's own current value. The transform form backs relative math (`2x`, `50%`) over a
 * multi-selection whose layers differ, and still lands as a single undo entry.
 */
export type LayerNumber = number | ((current: number) => number);

function resolveLayerNumber(value: LayerNumber, current: number): number {
  return typeof value === 'function' ? value(current) : value;
}

export class CompositorView {
  /**
   * The layers held by the last copy, shared across every compositor for the
   * session. The token also goes on the OS clipboard, which is the only way to
   * find out whether the user has copied something else since. It does not
   * survive a reload or a second tab.
   */
  private static copiedLayers: { token: string; payload: string; count: number } | undefined;

  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  /**
   * Writes a partial across a selection as one undo entry. Each layer reaches
   * `apply` as the panel shows it, and goes to the engine the same way, so a
   * field that reads the same either way is written the same way for every kind
   * of layer.
   */
  private applyLayerEdits(layerIds: LayerId[], apply: (layer: Layer) => Partial<Layer> | undefined, ongoing = false) {
    this.graph.cancelOngoingAction();

    this.graph.edit(() => {
      const entry = UndoRedoEntry.empty();
      for (const layerId of layerIds) {
        const stored = this.getLayer(layerId);
        if (!stored) continue;
        const layer = this.layerShowingItsBox(layerId, stored);
        const partial = apply(layer);
        if (partial !== undefined) {
          entry.add(this.graph.wasmChange((wasm) => wasm.updateLayer(this.nodeId, layerId, { ...layer, ...partial })));
        }
      }
      return entry;
    }, ongoing);
  }

  private applyStrokeOrFillEdits(
    layerIds: LayerId[],
    apply: (layer: Layer) => Partial<Layer> | undefined,
    ongoing = false,
  ) {
    this.applyLayerEdits(layerIds, apply, ongoing);
    this.hideVectorEditInterfaceBriefly();
  }

  /**
   * Takes the vector edit interface off screen and puts it back
   * {@link VECTOR_EDIT_INTERFACE_HIDE_MS} later. A further call before then
   * holds it off and restarts the wait, so a stream of edits hides it once.
   */
  private hideVectorEditInterfaceBriefly() {
    const pending = vectorEditInterfaceTimers.get(this.nodeId);
    if (pending === undefined) {
      this.showHideVectorEditInterface(false);
    } else {
      clearTimeout(pending);
    }

    vectorEditInterfaceTimers.set(
      this.nodeId,
      setTimeout(() => {
        vectorEditInterfaceTimers.delete(this.nodeId);
        this.showHideVectorEditInterface(true);
      }, VECTOR_EDIT_INTERFACE_HIDE_MS),
    );
  }

  /**
   * The layer as the panel shows it. A group carries no position or size of its
   * own, so those two fields stand in for the box it derives from what it holds:
   * a write of them says where that box goes, and `updateLayer` takes it there
   * by moving or scaling the contents. A write of anything else carries the box
   * back unchanged and so moves nothing.
   *
   * Every other layer, and a group holding no geometry to bound, is itself.
   */
  private layerShowingItsBox(layerId: LayerId, layer: Layer): Layer {
    if (layer.kind.type !== 'group') return layer;
    const box = this.getGroupBox(layerId);
    if (box === undefined) return layer;
    return { ...layer, position: { x: box.x, y: box.y }, dimension: { x: box.width, y: box.height } };
  }

  private getLayer(layerId: LayerId): Layer | undefined {
    return this.graph.store.getNodeDataNullable<CompositorNodeV3>(this.nodeId)?.data.layers[layerId];
  }

  /** Resolve on invocation, not render: computed inputs export the connected node's original image. */
  getImageSource(layerId: LayerId): CompositorImageSource | undefined {
    const data = this.graph.store.getNodeDataNullable<CompositorNodeV3>(this.nodeId)?.data;
    const layer = data?.layers[layerId];
    if (!data || !layer || layer.kind.type !== 'image') return undefined;
    const resource = resolveLayerResource(data, layer);
    if (resource?.type !== 'image') return undefined;
    if (resource.content.type === 'value')
      return { kind: 'owned', file: resource.content.data, binding: { resource: layer.kind.resource } };
    const input = this.graph.evaluateInput(this.nodeId, resource.content.data);
    if (input?.type !== 'image') return undefined;
    const connection = this.graph.getNodeInput(this.nodeId, resource.content.data);
    const binding = { resource: layer.kind.resource, input: resource.content.data, connection };
    if (input.value.type === 'file') return { kind: 'wired_file', file: input.value, binding };
    const rendered =
      connection && this.graph.wasm.call((wasm) => wasm.exportNodeRender(connection.nodeId, connection.outputId));
    return rendered
      ? {
          kind: 'wired_computed',
          binding,
          file: {
            url: rendered.data,
            width: rendered.width,
            height: rendered.height,
            id: undefined,
            thumbnailUrl: undefined,
            publicId: undefined,
          },
        }
      : undefined;
  }

  zoomLevel(): number {
    return this.graph.getUIState().compositor[this.nodeId]?.zoomLevel ?? 1;
  }

  setZoomLevel(zoom: number) {
    this.graph.wasm.call((wasm) => {
      wasm.setZoomLevel(this.nodeId, zoom);
    });
  }

  zoomToFit() {
    this.graph.wasm.call((wasm) => {
      wasm.zoomToFit(this.nodeId);
    });
  }

  zoomToLayer(layerId: LayerId) {
    this.graph.wasm.call((wasm) => wasm.zoomToLayer(this.nodeId, layerId));
  }

  setCompositorAiAction(layerId: LayerId, actionType?: CompositorAiActionKind) {
    this.graph.wasm.call((wasm) => wasm.setCompositorAiAction(this.nodeId, layerId, actionType));
  }

  onSubmitCompositorAiAction(): CompositorAiSubmitResult | undefined {
    return this.graph.wasm.call((wasm) => wasm.onSubmitCompositorAiAction(this.nodeId));
  }

  onDoneCompositorAiAction(layerId: LayerId, discardActionState: boolean) {
    this.graph.wasm.call((wasm) => wasm.onDoneCompositorAiAction(this.nodeId, layerId, discardActionState));
  }

  setCompositorAiBrushSize(size: number) {
    this.graph.wasm.call((wasm) => wasm.setCompositorAiBrushSize(this.nodeId, size));
  }

  clearCompositorAiMask() {
    this.graph.wasm.call((wasm) => wasm.clearCompositorAiMask(this.nodeId));
  }

  setCompositorAiOutpaintAspectRatio(ratio?: number) {
    this.graph.wasm.call((wasm) => wasm.setCompositorAiOutpaintAspectRatio(this.nodeId, ratio));
  }

  fitCompositorAiOutpaintToImage() {
    this.graph.wasm.call((wasm) => wasm.fitCompositorAiOutpaintToImage(this.nodeId));
  }

  setToolMode(mode: ToolMode) {
    this.graph.wasm.call((wasm) => wasm.setToolMode(this.nodeId, mode));
  }

  isVectorEditActive(): boolean {
    return this.graph.wasm.call((wasm) => wasm.isVectorEditActive(this.nodeId)) ?? false;
  }

  showHideVectorEditInterface(shouldShow: boolean): void {
    this.graph.wasm.call((wasm) => wasm.showHideVectorEditInterface(this.nodeId, shouldShow));
  }

  /** Whether the layer is locked, either directly or through a locked ancestor. */
  isLayerLocked(layerId: LayerId): boolean {
    return this.graph.wasm.call((wasm) => wasm.isLayerLocked(this.nodeId, layerId)) ?? false;
  }

  /**
   * Whether a locked group holds the layer, whatever its own lock says. Such a
   * layer cannot release itself, only the group can.
   */
  isLayerLockedByAncestor(layerId: LayerId): boolean {
    return this.graph.wasm.call((wasm) => wasm.isLayerLockedByAncestor(this.nodeId, layerId)) ?? false;
  }

  setStageDimensions(width: number, height: number, ongoing = false) {
    this.graph.wasmEdit((wasm) => wasm.setStageDimensions(this.nodeId, width, height), ongoing);
  }

  setStageAspectRatioLocked(locked: boolean) {
    this.graph.wasmEdit((wasm) => wasm.setStageAspectRatioLocked(this.nodeId, locked));
  }

  setCompositorDuration(duration: number) {
    this.graph.wasmEdit((wasm) => wasm.setCompositorDuration(this.nodeId, duration));
  }

  /** Resolve the current source duration for owned or connected video/audio. */
  private getMediaSourceDuration(compositor: CompositorNodeV3['data'], layer: Layer): number | undefined {
    const resource = resolveLayerResource(compositor, layer);
    if (resource?.type !== 'video' && resource?.type !== 'audio') return undefined;
    if (resource.content.type === 'value') return resource.content.data.duration;

    const input = this.graph.evaluateInput(this.nodeId, resource.content.data);
    if (input?.type !== 'video' && input?.type !== 'audio') return undefined;
    return input.value.duration;
  }

  /** The trimmed content span, or undefined for unresolved media with no timeline track. */
  private getLayerContentDuration(compositor: CompositorNodeV3['data'], layer: Layer): number | undefined {
    const duration = layer.duration ?? compositor.duration;
    if (layer.kind.type !== 'video' && layer.kind.type !== 'audio') return duration;

    const sourceDuration = this.getMediaSourceDuration(compositor, layer);
    if (sourceDuration === undefined || !Number.isFinite(sourceDuration)) return undefined;

    const trimmedDuration = Math.max(0, sourceDuration - layer.kind.trimStart - layer.kind.trimEnd);
    return Math.min(duration, trimmedDuration);
  }

  /** Fit to content within ancestor playback windows, excluding empty group tails. */
  fitDurationToContent() {
    const compositor = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;
    const { layers, layerOrder, duration } = compositor;
    let earliestStart = Infinity;
    let latestEnd: number | undefined;

    const visit = (ids: LayerId[], parentStart: number, windowStart: number, windowEnd: number) => {
      for (const id of ids) {
        const layer = layers[id];
        if (!layer) continue;

        const start = parentStart + (layer.startTime ?? 0);
        const end = Math.min(windowEnd, start + (layer.duration ?? duration));
        const visibleStart = Math.max(windowStart, start);
        if (end <= visibleStart) continue;

        const contentDuration = this.getLayerContentDuration(compositor, layer);
        if (contentDuration === undefined) continue;

        visit(layer.children, start, visibleStart, end);
        if (layer.kind.type === 'group') continue;

        const contentEnd = Math.min(end, start + contentDuration);
        if (contentEnd <= visibleStart) continue;
        earliestStart = Math.min(earliestStart, visibleStart);
        latestEnd = Math.max(latestEnd ?? 0, contentEnd);
      }
    };

    visit(layerOrder, 0, 0, Infinity);
    if (latestEnd === undefined) return;
    const fittedDuration = latestEnd - earliestStart;
    if (earliestStart === 0 && fittedDuration === duration) return;

    this.graph.edit(() => {
      const entry = UndoRedoEntry.empty();
      for (const id of layerOrder) {
        const layer = layers[id];
        if (!layer || earliestStart === 0) continue;
        /** Moving roots preserves child offsets and keeps group playback windows aligned. */
        entry.add(
          this.graph.wasmChange((wasm) =>
            wasm.updateLayer(this.nodeId, id, {
              ...layer,
              startTime: (layer.startTime ?? 0) - earliestStart,
            }),
          ),
        );
      }
      entry.add(this.graph.wasmChange((wasm) => wasm.setCompositorDuration(this.nodeId, fittedDuration)));
      return entry;
    });
  }

  setCompositorFps(fps: number, ongoing = false) {
    this.graph.wasmEdit((wasm) => wasm.setCompositorFps(this.nodeId, fps), ongoing);
  }

  setIsCompositorTimelineEnabled(enabled: boolean) {
    this.graph.wasmEdit((wasm) => wasm.setIsCompositorTimelineEnabled(this.nodeId, enabled));
  }

  setCompositorBackgroundColor(color?: WebColor | null, ongoing = false) {
    this.graph.wasmEdit((wasm) => wasm.setCompositorBackgroundColor(this.nodeId, color), ongoing);
  }

  setIsLayerLocked(layerIds: LayerId[], locked: boolean) {
    this.applyLayerEdits(layerIds, (layer) => (layer.locked === locked ? undefined : { locked }));
  }

  /**
   * The box a group displays: x/y/width/height from descendant geometry in
   * group space. The angle is not part of it, since a group carries its own.
   * `undefined` for any other kind of layer, which shows its own fields instead.
   */
  getGroupBox(layerId: LayerId): GroupBox | undefined {
    return this.graph.wasm.call((wasm) => wasm.compositorGroupBox(this.nodeId, layerId)) ?? undefined;
  }

  setLayerPositionX(layerIds: LayerId[], x: LayerNumber, ongoing = false) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        const next = resolveLayerNumber(x, layer.position.x);
        return Math.abs(layer.position.x - next) < 0.0001 ? undefined : { position: { x: next, y: layer.position.y } };
      },
      ongoing,
    );
  }

  setLayerPositionY(layerIds: LayerId[], y: LayerNumber, ongoing = false) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        const next = resolveLayerNumber(y, layer.position.y);
        return Math.abs(layer.position.y - next) < 0.0001 ? undefined : { position: { x: layer.position.x, y: next } };
      },
      ongoing,
    );
  }

  offsetLayerPositions(layerIds: LayerId[], dx: number, dy: number) {
    this.graph.wasmEdit((wasm) => wasm.offsetCompositorLayerPositions(this.nodeId, layerIds, dx, dy));
  }

  /**
   * A group carries its own angle, unlike the rest of its box. The engine takes
   * a group's turn about the center of that box, the point the rotation handle
   * turns about.
   */
  setLayerRotation(layerIds: LayerId[], angle: LayerNumber, ongoing = false) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        const next = resolveLayerNumber(angle, layer.rotation);
        return Math.abs(layer.rotation - next) < 0.0001 ? undefined : { rotation: next };
      },
      ongoing,
    );
  }

  setLayerWidth(layerIds: LayerId[], width: LayerNumber, ongoing = false) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        const next = resolveLayerNumber(width, layer.dimension.x);
        return Math.abs(layer.dimension.x - next) < 0.0001
          ? undefined
          : {
              dimension: {
                x: next,
                y: layer.lockedAspectRatio ? (layer.dimension.y / layer.dimension.x) * next : layer.dimension.y,
              },
            };
      },
      ongoing,
    );
  }

  setLayerHeight(layerIds: LayerId[], height: LayerNumber, ongoing = false) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        const next = resolveLayerNumber(height, layer.dimension.y);
        return Math.abs(layer.dimension.y - next) < 0.0001
          ? undefined
          : {
              dimension: {
                x: layer.lockedAspectRatio ? (layer.dimension.x / layer.dimension.y) * next : layer.dimension.x,
                y: next,
              },
            };
      },
      ongoing,
    );
  }

  setLayerColor(layerIds: LayerId[], value: { r: number; g: number; b: number; a: number }, ongoing = false) {
    this.applyStrokeOrFillEdits(
      layerIds,
      (layer) => {
        if (layer.fill?.type === 'image') return undefined;
        const fill = layer.fill?.type === 'color' ? layer.fill.color : { r: 255, g: 255, b: 255, a: 1 };
        const a = Math.max(0, Math.min(1, value.a));
        return layer.fill?.type === 'color' &&
          Math.abs(fill.r - value.r) < 0.0001 &&
          Math.abs(fill.g - value.g) < 0.0001 &&
          Math.abs(fill.b - value.b) < 0.0001 &&
          Math.abs(fill.a - a) < 0.0001
          ? undefined
          : {
              // Editing a hidden fill unhides it — a change the user cannot see is
              // almost never what they meant.
              fill: { type: 'color', color: { r: value.r, g: value.g, b: value.b, a }, visible: true },
            };
      },
      ongoing,
    );
  }

  convertImagesToShapes(layerIds: LayerId[]) {
    this.graph.wasmEdit((wasm) => wasm.convertImageLayersToShapes(this.nodeId, layerIds));
  }

  isPerspectiveShape(layer: Layer) {
    return this.graph.wasm.call((wasm) => wasm.isPerspectiveShape(layer)) ?? false;
  }

  setLayerFillPerspective(layerIds: LayerId[], perspective: boolean) {
    this.applyStrokeOrFillEdits(layerIds, (layer) =>
      layer.kind.type === 'shape' && layer.fill?.type === 'image' && (layer.fill.perspective ?? false) !== perspective
        ? { fill: { ...layer.fill, perspective } }
        : undefined,
    );
  }

  addLayerFill(layerIds: LayerId[]) {
    this.applyStrokeOrFillEdits(layerIds, (layer) =>
      layer.kind.type === 'shape' && (!layer.fill || layer.fill.type === 'none')
        ? { fill: { type: 'color', color: { r: 255, g: 255, b: 255, a: 1 }, visible: true } }
        : undefined,
    );
  }

  removeLayerFill(layerIds: LayerId[]) {
    this.applyStrokeOrFillEdits(layerIds, (layer) =>
      layer.kind.type === 'shape' && layer.fill?.type !== 'none' ? { fill: { type: 'none' } } : undefined,
    );
  }

  setLayerFillVisible(layerIds: LayerId[], visible: boolean) {
    this.applyStrokeOrFillEdits(layerIds, (layer) =>
      (layer.fill?.type === 'color' || layer.fill?.type === 'image') && (layer.fill.visible ?? true) !== visible
        ? { fill: { ...layer.fill, visible } }
        : undefined,
    );
  }

  /**
   * Adds a stroke to every selected layer that has none, using the default
   * weight and position.
   */
  addLayerStroke(layerIds: LayerId[]) {
    this.applyStrokeOrFillEdits(layerIds, (layer) =>
      layer.stroke?.type === 'line' ? undefined : { stroke: DEFAULT_STROKE },
    );
  }

  removeLayerStroke(layerIds: LayerId[]) {
    this.applyStrokeOrFillEdits(layerIds, (layer) =>
      layer.stroke?.type === 'line' ? { stroke: { type: 'none' } } : undefined,
    );
  }

  setLayerStrokeVisible(layerIds: LayerId[], visible: boolean) {
    this.applyStrokeOrFillEdits(layerIds, (layer) =>
      layer.stroke?.type === 'line' && (layer.stroke.visible ?? true) !== visible
        ? { stroke: { ...layer.stroke, visible } }
        : undefined,
    );
  }

  setLayerStrokeColor(layerIds: LayerId[], value: { r: number; g: number; b: number; a: number }, ongoing = false) {
    this.applyStrokeOrFillEdits(
      layerIds,
      (layer) => {
        const stroke = layer.stroke?.type === 'line' ? layer.stroke : DEFAULT_STROKE;
        const { color } = stroke;
        const a = Math.max(0, Math.min(1, value.a));
        return Math.abs(color.r - value.r) < 0.0001 &&
          Math.abs(color.g - value.g) < 0.0001 &&
          Math.abs(color.b - value.b) < 0.0001 &&
          Math.abs(color.a - a) < 0.0001
          ? undefined
          : { stroke: { ...stroke, color: { r: value.r, g: value.g, b: value.b, a }, visible: true } };
      },
      ongoing,
    );
  }

  setLayerStrokeWeight(layerIds: LayerId[], weight: LayerNumber, ongoing = false) {
    this.applyStrokeOrFillEdits(
      layerIds,
      (layer) => {
        const stroke = layer.stroke?.type === 'line' ? layer.stroke : DEFAULT_STROKE;
        const next = Math.max(0, resolveLayerNumber(weight, stroke.weight));
        return Math.abs(stroke.weight - next) < 0.0001
          ? undefined
          : { stroke: { ...stroke, weight: next, visible: true } };
      },
      ongoing,
    );
  }

  setLayerStrokePosition(layerIds: LayerId[], position: StrokePosition, ongoing = false) {
    this.applyStrokeOrFillEdits(
      layerIds,
      (layer) => {
        const stroke = layer.stroke?.type === 'line' ? layer.stroke : DEFAULT_STROKE;
        return stroke.position === position ? undefined : { stroke: { ...stroke, position, visible: true } };
      },
      ongoing,
    );
  }

  setLayerStrokeJoin(layerIds: LayerId[], join: StrokeJoin, ongoing = false) {
    this.applyStrokeOrFillEdits(
      layerIds,
      (layer) => {
        const stroke = layer.stroke?.type === 'line' ? layer.stroke : DEFAULT_STROKE;
        return stroke.join === join ? undefined : { stroke: { ...stroke, join, visible: true } };
      },
      ongoing,
    );
  }

  setLayerStrokeCap(layerIds: LayerId[], cap: StrokeCap, ongoing = false) {
    this.applyStrokeOrFillEdits(
      layerIds,
      (layer) => {
        const stroke = layer.stroke?.type === 'line' ? layer.stroke : DEFAULT_STROKE;
        return stroke.cap === cap ? undefined : { stroke: { ...stroke, cap, visible: true } };
      },
      ongoing,
    );
  }

  setLayerOpacity(layerIds: LayerId[], nextOpacity: LayerNumber, ongoing = false) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        const opacityProperty = layer.opacity ?? { type: 'constant' as const, value: 1 };
        const fade = propertyToFade(opacityProperty, layer.duration);
        if (fade.kind === 'unrecognized') return undefined;
        const clamped = Math.max(0, Math.min(1, resolveLayerNumber(nextOpacity, fade.fadedValue)));
        if (Math.abs(fade.fadedValue - clamped) < 0.0001) return undefined;
        const opacity = editFadedProperty(opacityProperty, layer.duration, clamped);
        return opacity ? { opacity } : undefined;
      },
      ongoing,
    );
  }

  private applyLayerFadeEdits(
    layerIds: LayerId[],
    target: 'opacity' | 'volume' | 'auto',
    edit: (property: Property<number>, layer: Layer) => Property<number> | undefined,
    ongoing = false,
  ) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        if (target === 'volume' && layer.kind.type !== 'audio') return undefined;
        const property =
          target !== 'opacity' && layer.kind.type === 'audio'
            ? layer.kind.volume
            : (layer.opacity ?? { type: 'constant' as const, value: 1 });
        const next = edit(property, layer);
        if (!next || next === property) return undefined;
        return target !== 'opacity' && layer.kind.type === 'audio'
          ? { kind: { ...layer.kind, volume: next } }
          : { opacity: next };
      },
      ongoing,
    );
  }

  toggleLayerFade(layerIds: LayerId[], edge: 'in' | 'out', target: 'opacity' | 'volume' | 'auto' = 'auto') {
    this.applyLayerFadeEdits(layerIds, target, (property, layer) => {
      const fade = propertyToFade(property, layer.duration);
      if (fade.kind === 'unrecognized') return undefined;
      if (fade.kind === 'constant') {
        const fadeDuration = Math.min(0.5, layer.duration / 2);
        const next = createFadeProperty(
          fade.fadedValue,
          layer.duration,
          edge === 'in' ? fadeDuration : undefined,
          edge === 'out' ? fadeDuration : undefined,
        );
        return next;
      }

      let fadeInDuration = fade.fadeInDuration;
      let fadeOutDuration = fade.fadeOutDuration;
      const existingDuration = edge === 'in' ? fadeInDuration : fadeOutDuration;
      if (existingDuration !== undefined) {
        if (edge === 'in') fadeInDuration = undefined;
        else fadeOutDuration = undefined;
      } else {
        const addedDuration = Math.min(0.5, layer.duration / 2);
        if (edge === 'in') {
          fadeInDuration = addedDuration;
          if (fadeOutDuration !== undefined)
            fadeOutDuration = Math.min(fadeOutDuration, layer.duration - addedDuration);
        } else {
          fadeOutDuration = addedDuration;
          if (fadeInDuration !== undefined) fadeInDuration = Math.min(fadeInDuration, layer.duration - addedDuration);
        }
      }

      const next =
        fadeInDuration === undefined && fadeOutDuration === undefined
          ? { type: 'constant' as const, value: fade.fadedValue }
          : editFadeDurations(property, layer.duration, fadeInDuration, fadeOutDuration);
      return next;
    });
  }

  setLayerFadeDuration(
    layerIds: LayerId[],
    edge: 'in' | 'out',
    nextDuration: number,
    target: 'opacity' | 'volume',
    ongoing = false,
  ) {
    this.applyLayerFadeEdits(
      layerIds,
      target,
      (property, layer) => {
        const fade = propertyToFade(property, layer.duration);
        if (fade.kind !== 'fade') return undefined;
        const currentDuration = edge === 'in' ? fade.fadeInDuration : fade.fadeOutDuration;
        if (currentDuration === undefined) return undefined;
        const otherDuration = edge === 'in' ? fade.fadeOutDuration : fade.fadeInDuration;
        const clamped = Math.max(0.01, Math.min(nextDuration, layer.duration - (otherDuration ?? 0)));
        const next = editFadeDurations(
          property,
          layer.duration,
          edge === 'in' ? clamped : fade.fadeInDuration,
          edge === 'out' ? clamped : fade.fadeOutDuration,
        );
        return next;
      },
      ongoing,
    );
  }

  /** Timeline gestures may remove an edge at zero or mirror both edges as one undoable edit. */
  editLayerOpacityFade(layerId: LayerId, edge: 'in' | 'out', nextDuration: number, ongoing: boolean, mirror = false) {
    if (!Number.isFinite(nextDuration)) return;
    this.applyLayerEdits(
      [layerId],
      (layer) => {
        const property = layer.opacity ?? { type: 'constant' as const, value: 1 };
        const fade = propertyToFade(property, layer.duration);
        if (fade.kind !== 'fade') return undefined;
        if ((edge === 'in' ? fade.fadeInDuration : fade.fadeOutDuration) === undefined) return undefined;
        const otherDuration = edge === 'in' ? fade.fadeOutDuration : fade.fadeInDuration;
        const limit = mirror ? layer.duration / 2 : layer.duration;
        const value = Math.max(0, Math.min(nextDuration, limit));
        /** Dragging past the opposite fade moves both boundaries to the dragged handle's time. */
        const otherValue =
          otherDuration === undefined ? undefined : Math.min(otherDuration, layer.duration - value) || undefined;
        const fadeIn = mirror || edge === 'in' ? value || undefined : otherValue;
        const fadeOut = mirror || edge === 'out' ? value || undefined : otherValue;
        let opacity =
          fadeIn === undefined && fadeOut === undefined
            ? { type: 'constant' as const, value: fade.fadedValue }
            : editFadeDurations(property, layer.duration, fadeIn, fadeOut);
        if (!opacity) return undefined;
        if (fadeIn !== undefined && fade.fadeInDuration === undefined)
          opacity = editFadeEasing(opacity, layer.duration, 'in', 'linear') ?? opacity;
        if (fadeOut !== undefined && fade.fadeOutDuration === undefined)
          opacity = editFadeEasing(opacity, layer.duration, 'out', 'linear') ?? opacity;
        if (fadeIn !== undefined && (mirror || edge === 'in'))
          opacity = editFadeEnabled(opacity, layer.duration, 'in', true) ?? opacity;
        if (fadeOut !== undefined && (mirror || edge === 'out'))
          opacity = editFadeEnabled(opacity, layer.duration, 'out', true) ?? opacity;
        return { opacity };
      },
      ongoing,
    );
  }

  setLayerFadeEasing(layerIds: LayerId[], edge: 'in' | 'out', easing: Easing, target: 'opacity' | 'volume') {
    this.applyLayerFadeEdits(layerIds, target, (property, layer) => {
      const next = editFadeEasing(property, layer.duration, edge, easing);
      return next;
    });
  }

  setLayerFadeEnabled(layerIds: LayerId[], edge: 'in' | 'out', enabled: boolean, target: 'opacity' | 'volume') {
    this.applyLayerFadeEdits(layerIds, target, (property, layer) => {
      const next = editFadeEnabled(property, layer.duration, edge, enabled);
      return next;
    });
  }

  setLayerBlendMode(layerIds: LayerId[], blendMode: BlendMode, ongoing: boolean) {
    this.applyLayerEdits(layerIds, (layer) => (layer.blendMode === blendMode ? undefined : { blendMode }), ongoing);
  }

  setLayerImageMode(layerIds: LayerId[], imageMode: ImageMode, ongoing: boolean) {
    this.applyLayerEdits(
      layerIds,
      (layer) =>
        layer.kind.type === 'shape' && layer.fill?.type === 'image'
          ? (layer.fill.imageMode ?? 'stretch') === imageMode
            ? undefined
            : { fill: { ...layer.fill, imageMode } }
          : (layer.kind.type !== 'image' && layer.kind.type !== 'video') || layer.kind.imageMode === imageMode
            ? undefined
            : { kind: { ...layer.kind, imageMode } },
      ongoing,
    );
  }

  /**
   * Adds an effect of `effectType`, with its options at their defaults, at the
   * top of the layer's effects.
   */
  addEffect(layerId: LayerId, effectType: EffectType) {
    this.graph.wasmEdit((wasm) => wasm.addEffect(this.nodeId, layerId, effectType));
  }

  /** Removes the layer's effect at `index`. */
  removeEffect(layerId: LayerId, index: number) {
    this.graph.wasmEdit((wasm) => wasm.removeEffect(this.nodeId, layerId, index));
  }

  /** Draws the layer's effect at `index`, or stops drawing it, keeping the effect either way. */
  setEffectEnabled(layerId: LayerId, index: number, enabled: boolean) {
    this.graph.wasmEdit((wasm) => wasm.setEffectEnabled(this.nodeId, layerId, index, enabled));
  }

  /**
   * Moves the layer's effect at `index` to `toIndex`, a place named in the list
   * as it stands with the effect still in it — which is what a drop between two
   * rows points at. Effects are drawn in the order they are listed, so this is
   * what changes the order they are applied in.
   */
  moveEffect(layerId: LayerId, index: number, toIndex: number) {
    this.graph.wasmEdit((wasm) => wasm.moveEffect(this.nodeId, layerId, index, toIndex));
  }

  /**
   * Replaces the options of the layer's effect at `index`. A drag through a
   * slider writes `ongoing`, so the whole drag lands as one undo entry.
   */
  setEffectKind(layerId: LayerId, index: number, kind: EffectKind, ongoing = false) {
    this.graph.wasmEdit((wasm) => wasm.setEffectKind(this.nodeId, layerId, index, kind), ongoing);
  }

  setLayerVisible(layerIds: LayerId[], visible: boolean) {
    this.applyLayerEdits(layerIds, (layer) => (layer.visible === visible ? undefined : { visible }));
  }

  setLayerMuted(layerIds: LayerId[], muted: boolean) {
    this.applyLayerEdits(layerIds, (layer) =>
      (layer.kind.type !== 'video' && layer.kind.type !== 'audio') || layer.kind.muted === muted
        ? undefined
        : { kind: { ...layer.kind, muted } },
    );
  }

  setLayerVolume(layerIds: LayerId[], volume: LayerNumber, ongoing: boolean) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        if (layer.kind.type !== 'video' && layer.kind.type !== 'audio') return undefined;
        const fade = propertyToFade(layer.kind.volume, layer.duration);
        const current = fade.kind === 'unrecognized' ? 1 : fade.fadedValue;
        const clamped = Math.max(0, Math.min(1, resolveLayerNumber(volume, current)));
        if (fade.kind === 'unrecognized') {
          return { kind: { ...layer.kind, volume: { type: 'constant', value: clamped } } };
        }
        if (Math.abs(current - clamped) < 0.0001) return undefined;
        const property = editFadedProperty(layer.kind.volume, layer.duration, clamped);
        return property ? { kind: { ...layer.kind, volume: property } } : undefined;
      },
      ongoing,
    );
  }

  setLayerStartTime(layerIds: LayerId[], startTime: number, ongoing: boolean) {
    this.applyLayerEdits(
      layerIds,
      (layer) => (Math.abs((layer.startTime ?? 0) - startTime) < 0.0001 ? undefined : { startTime }),
      ongoing,
    );
  }

  offsetLayerStartTimes(layerIds: LayerId[], delta: number, ongoing: boolean) {
    const { layers } = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;
    this.applyLayerEdits(
      selectedLayerRoots(layers, layerIds),
      (layer) => {
        if (Math.abs(delta) < 0.0001) return undefined;
        return { startTime: (layer.startTime ?? 0) + delta };
      },
      ongoing,
    );
  }

  /** Keeps recognized opacity and volume fades at the edges when the visible span changes. */
  private layerDurationEdit(layer: Layer, duration: number): Partial<Layer> {
    const remapFade = (property: Property<number>) => {
      const fade = propertyToFade(property, layer.duration);
      if (fade.kind !== 'fade') return undefined;

      let fadeInDuration = fade.fadeInDuration ?? 0;
      let fadeOutDuration = fade.fadeOutDuration ?? 0;
      const overlap = fadeInDuration + fadeOutDuration - duration;
      if (overlap > 0) {
        // Share the reduction equally until the shorter fade runs out of time.
        fadeInDuration = Math.max(0, Math.min(duration, fadeInDuration - overlap / 2));
        fadeOutDuration = duration - fadeInDuration;
      }
      // Keep a disabled one-sided fade recognizable when it fills the new span.
      const margin = Math.min(0.01, duration / 2);
      if (fadeOutDuration === 0 && !fade.fadeInEnabled) fadeInDuration = Math.min(fadeInDuration, duration - margin);
      if (fadeInDuration === 0 && !fade.fadeOutEnabled) fadeOutDuration = Math.min(fadeOutDuration, duration - margin);
      return editFadeDurations(
        property,
        layer.duration,
        fadeInDuration || undefined,
        fadeOutDuration || undefined,
        duration,
      );
    };
    const partial: Partial<Layer> = { duration };
    const opacity = remapFade(layer.opacity);
    if (opacity !== undefined) partial.opacity = opacity;
    if (layer.kind.type === 'audio' || layer.kind.type === 'video') {
      const volume = remapFade(layer.kind.volume);
      if (volume !== undefined) partial.kind = { ...layer.kind, volume };
    }
    return partial;
  }

  setLayerDuration(layerIds: LayerId[], duration: number, ongoing: boolean) {
    const clampedDuration = Math.max(duration, 0);
    this.applyLayerEdits(
      layerIds,
      (layer) =>
        Math.abs((layer.duration ?? 5) - clampedDuration) < 0.0001
          ? undefined
          : this.layerDurationEdit(layer, clampedDuration),
      ongoing,
    );
  }

  setVideoLayerTrimStart(layerIds: LayerId[], trimStart: number, ongoing: boolean) {
    const clampedTrimStart = Math.max(trimStart, 0);
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        return (layer.kind.type !== 'video' && layer.kind.type !== 'audio') ||
          Math.abs(layer.kind.trimStart - clampedTrimStart) < 0.0001
          ? undefined
          : { kind: { ...layer.kind, trimStart: clampedTrimStart } };
      },
      ongoing,
    );
  }

  setVideoLayerTrimEnd(layerIds: LayerId[], trimEnd: number, ongoing: boolean) {
    const clampedTrimEnd = Math.max(trimEnd, 0);
    this.applyLayerEdits(
      layerIds,
      (layer) =>
        (layer.kind.type !== 'video' && layer.kind.type !== 'audio') ||
        Math.abs(layer.kind.trimEnd - clampedTrimEnd) < 0.0001
          ? undefined
          : { kind: { ...layer.kind, trimEnd: clampedTrimEnd } },
      ongoing,
    );
  }

  offsetLayerTrim(layerIds: LayerId[], delta: number, edge: 'left' | 'right', ongoing: boolean) {
    const { layers } = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;
    this.applyLayerEdits(
      selectedLayerRoots(layers, layerIds),
      (layer) => {
        if (Math.abs(delta) < 0.0001) return undefined;
        const duration = Math.max((layer.duration ?? 5) + (edge === 'right' ? delta : -delta), 0);
        const partial = this.layerDurationEdit(layer, duration);
        const kind = partial.kind ?? layer.kind;
        if (edge === 'right') {
          if (kind.type === 'video' || kind.type === 'audio') {
            partial.kind = { ...kind, trimEnd: Math.max(kind.trimEnd - delta, 0) };
          }
        } else {
          partial.startTime = (layer.startTime ?? 0) + delta;
          if (kind.type === 'video' || kind.type === 'audio') {
            partial.kind = { ...kind, trimStart: Math.max(kind.trimStart + delta, 0) };
          }
        }
        return partial;
      },
      ongoing,
    );
  }

  setLayerTextSize(layerIds: LayerId[], size: LayerNumber, ongoing: boolean) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        if (layer.kind.type !== 'text') return undefined;
        const next = resolveLayerNumber(size, layer.kind.layoutOptions.size);
        return Math.abs(layer.kind.layoutOptions.size - next) < 0.0001
          ? undefined
          : { kind: { ...layer.kind, layoutOptions: { ...layer.kind.layoutOptions, size: next } } };
      },
      ongoing,
    );
  }

  setLayerTextLetterSpacing(layerIds: LayerId[], letterSpacing: LayerNumber, ongoing: boolean) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        if (layer.kind.type !== 'text') return undefined;
        const next = resolveLayerNumber(letterSpacing, layer.kind.layoutOptions.letterSpacing);
        return Math.abs(layer.kind.layoutOptions.letterSpacing - next) < 0.0001
          ? undefined
          : { kind: { ...layer.kind, layoutOptions: { ...layer.kind.layoutOptions, letterSpacing: next } } };
      },
      ongoing,
    );
  }

  setLayerTextLineHeight(layerIds: LayerId[], lineHeight: LayerNumber, ongoing: boolean) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        if (layer.kind.type !== 'text') return undefined;
        const next = resolveLayerNumber(lineHeight, layer.kind.layoutOptions.lineHeight);
        return Math.abs(layer.kind.layoutOptions.lineHeight - next) < 0.0001
          ? undefined
          : { kind: { ...layer.kind, layoutOptions: { ...layer.kind.layoutOptions, lineHeight: next } } };
      },
      ongoing,
    );
  }

  setLayerTextHorizontalAlign(layerIds: LayerId[], horizontalAlign: HorizontalAlign, ongoing: boolean) {
    this.applyLayerEdits(
      layerIds,
      (layer) =>
        layer.kind.type !== 'text' || layer.kind.layoutOptions.horizontalAlign === horizontalAlign
          ? undefined
          : { kind: { ...layer.kind, layoutOptions: { ...layer.kind.layoutOptions, horizontalAlign } } },
      ongoing,
    );
  }

  setLayerTextVerticalAlign(layerIds: LayerId[], verticalAlign: VerticalAlign, ongoing: boolean) {
    this.applyLayerEdits(
      layerIds,
      (layer) =>
        layer.kind.type !== 'text' || layer.kind.layoutOptions.verticalAlign === verticalAlign
          ? undefined
          : { kind: { ...layer.kind, layoutOptions: { ...layer.kind.layoutOptions, verticalAlign } } },
      ongoing,
    );
  }

  /**
   * Sets the text family. `styleCombinations` are the faces the family actually has, which a
   * workspace family only has some of: each layer lands on its current weight and slant when the
   * family has that face, and on the nearest one when it does not, so the family is never left on
   * a face nothing was uploaded for. Passed empty — built-ins, and families whose uploads are not
   * known — weight and slant are left as they are.
   */
  setLayerTextFont(
    layerIds: LayerId[],
    font: Font,
    ongoing: boolean,
    styleCombinations: readonly FontStyleCombination[] = [],
  ) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        if (layer.kind.type !== 'text') {
          return undefined;
        }
        const fontOptions = layer.kind.fontOptions;
        const current = { weight: fontOptions.weight, isItalic: fontOptions.italic };
        const face = nearestFontStyleCombination(styleCombinations, current) ?? current;
        const unchanged =
          fontOptions.font.type === font.type &&
          getFontFamilyKey(fontOptions.font) === getFontFamilyKey(font) &&
          fontOptions.weight === face.weight &&
          fontOptions.italic === face.isItalic;
        return unchanged
          ? undefined
          : {
              kind: {
                ...layer.kind,
                fontOptions: { ...fontOptions, font, weight: face.weight, italic: face.isItalic },
              },
            };
      },
      ongoing,
    );
    this.graph.loadFontResources();
  }

  /**
   * The face a text layer lands on for a change made on one axis: a workspace family has only the
   * faces uploaded for it, so the other axis follows along to reach one of them. A built-in, and a
   * family the workspace lists no uploads for, takes the change as asked.
   */
  private resolvedTextFace(
    fontOptions: FontOptions,
    desired: FontStyleCombination,
    hold: 'slant' | 'weight',
    workspaceFonts: readonly WorkspaceFontFamily[],
  ): FontStyleCombination {
    const styleCombinations =
      fontOptions.font.type === 'custom'
        ? styleCombinationsForFamily(workspaceFonts, getFontFamilyKey(fontOptions.font))
        : [];
    return nearestFontStyleCombination(styleCombinations, desired, hold) ?? desired;
  }

  /**
   * Sets the text weight, carrying the slant to one the family has at that weight: a family
   * uploaded as regular and bold italic turns italic on the way to bold, a bold upright being a
   * face it has not got.
   */
  setLayerTextWeight(
    layerIds: LayerId[],
    weight: number,
    ongoing: boolean,
    workspaceFonts: readonly WorkspaceFontFamily[] = [],
  ) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        if (layer.kind.type !== 'text') {
          return undefined;
        }
        const fontOptions = layer.kind.fontOptions;
        const face = this.resolvedTextFace(
          fontOptions,
          { weight, isItalic: fontOptions.italic },
          'weight',
          workspaceFonts,
        );
        return Math.abs(fontOptions.weight - face.weight) < 0.0001 && fontOptions.italic === face.isItalic
          ? undefined
          : { kind: { ...layer.kind, fontOptions: { ...fontOptions, weight: face.weight, italic: face.isItalic } } };
      },
      ongoing,
    );
    this.graph.loadFontResources();
  }

  /**
   * Sets the text slant, carrying the weight to the nearest the family has in it: a family
   * uploaded as regular italic and bold upright drops to regular on the way to italic.
   */
  setLayerTextItalic(
    layerIds: LayerId[],
    italic: boolean,
    ongoing: boolean,
    workspaceFonts: readonly WorkspaceFontFamily[] = [],
  ) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        if (layer.kind.type !== 'text') {
          return undefined;
        }
        const fontOptions = layer.kind.fontOptions;
        const face = this.resolvedTextFace(
          fontOptions,
          { weight: fontOptions.weight, isItalic: italic },
          'slant',
          workspaceFonts,
        );
        return fontOptions.italic === face.isItalic && Math.abs(fontOptions.weight - face.weight) < 0.0001
          ? undefined
          : { kind: { ...layer.kind, fontOptions: { ...fontOptions, weight: face.weight, italic: face.isItalic } } };
      },
      ongoing,
    );
    this.graph.loadFontResources();
  }

  /** Sets text font, weight, and italic in a single WASM edit (one undo step). */
  setLayerTextFontFace(layerIds: LayerId[], font: Font, weight: number, italic: boolean, ongoing = false) {
    this.applyLayerEdits(
      layerIds,
      (layer) => {
        if (layer.kind.type !== 'text') {
          return undefined;
        }
        const fo = layer.kind.fontOptions;
        return {
          kind: {
            ...layer.kind,
            fontOptions: {
              ...fo,
              font,
              weight,
              italic,
            },
          },
        };
      },
      ongoing,
    );
    this.graph.loadFontResources();
  }

  /**
   * Slants the style dropdown may offer. A custom family lists the slants uploaded at the
   * layer's current weight, so a weight that exists only in italic does not offer normal.
   * A built-in, and a custom family whose uploads are not listed, keeps the engine's
   * family-wide answer.
   */
  italicSupport(layerId: LayerId, workspaceFonts: readonly WorkspaceFontFamily[] = []): boolean[] {
    const layer = this.getLayer(layerId);

    if (!layer || layer.kind.type !== 'text') {
      return [];
    }
    const fontOptions = layer.kind.fontOptions;
    if (fontOptions.font.type === 'custom') {
      const atWeight = italicOptionsAtWeight(
        styleCombinationsForFamily(workspaceFonts, getFontFamilyKey(fontOptions.font)),
        fontOptions.weight,
      );
      if (atWeight.length > 0) {
        return atWeight;
      }
    }
    return (
      this.graph.wasm.call((wasm) => {
        const support = wasm.italicSupport(fontOptions);
        return Object.values(support).map((b) => b === 1);
      }) ?? []
    );
  }

  weightSupport(layerId: LayerId): number[] {
    const layer = this.getLayer(layerId);

    if (!layer || layer.kind.type !== 'text') {
      return [];
    }
    const font = layer.kind.fontOptions;
    return (
      this.graph.wasm.call((wasm) => {
        return Array.from(wasm.weightSupport(font));
      }) ?? []
    );
  }

  /** Moves layers to `index` within `parentId` (top-level when `parentId` is null). */
  moveLayers(layerIds: LayerId[], parentId: LayerId | null, index: number) {
    // A single-layer move onto its own current slot resolves to no change.
    if (layerIds.length === 1) {
      const slot = this.layerSlot(layerIds[0]);
      if (slot && slot.parentId === parentId && (index === slot.index || index === slot.index + 1)) {
        return;
      }
    }
    this.graph.wasmEdit((wasm) => wasm.moveLayers(this.nodeId, layerIds, parentId ?? undefined, index));
  }

  /** Uses `sourceLayerId` as an alpha matte for `targetLayerId`. */
  setLayerMask(sourceLayerId: LayerId, targetLayerId: LayerId) {
    this.graph.wasmEdit((wasm) => wasm.setLayerMask(this.nodeId, sourceLayerId, targetLayerId));
  }

  /** Removes every mask from `layerId`. */
  breakLayerMasks(layerId: LayerId) {
    this.graph.wasmEdit((wasm) => wasm.breakLayerMasks(this.nodeId, layerId));
  }

  /**
   * The layer's current container and index: `parentId: null` for the top-level
   * order, otherwise the enclosing group and its position in that group's
   * children. Returns `undefined` if the layer isn't found.
   */
  private layerSlot(layerId: LayerId): { parentId: LayerId | null; index: number } | undefined {
    const data = this.graph.store.getNodeDataNullable<CompositorNodeV3>(this.nodeId)?.data;
    if (!data) return undefined;
    for (const [id, layer] of Object.entries(data.layers)) {
      const index = layer.children.indexOf(layerId);
      if (index !== -1) return { parentId: Number(id), index };
    }
    const index = data.layerOrder.indexOf(layerId);
    return index === -1 ? undefined : { parentId: null, index };
  }

  addTextLayer(text: string) {
    this.graph.wasmEdit((wasm) => wasm.addTextLayer(this.nodeId, text));
  }

  removeLayers(layerIds: LayerId[], keepWiredMedia: boolean) {
    this.graph.edit(() => {
      const undoEntry = this.graph.wasmChange((wasm) => {
        return wasm.removeLayers(this.nodeId, layerIds, keepWiredMedia);
      });
      undoEntry.add(
        UndoRedoEntry.fromNodes([
          {
            type: 'update',
            node: { ...this.graph.store.getNode(this.nodeId) },
          },
        ]),
      );
      return undoEntry;
    });
  }

  groupLayers(layerIds: LayerId[]) {
    this.graph.edit(() => this.graph.wasmChange((wasm) => wasm.groupLayers(this.nodeId, layerIds)));
  }

  ungroupLayers(layerIds: LayerId[]) {
    this.graph.edit(() => this.graph.wasmChange((wasm) => wasm.ungroupLayers(this.nodeId, layerIds)));
  }

  private selectedLayersIds(): LayerId[] {
    return this.graph.getUIState().compositor[this.nodeId]?.selectedLayers ?? [];
  }

  hasSelectedLayers(): boolean {
    return this.selectedLayersIds().length > 0;
  }

  setSelectedLayerIds(layerIds: LayerId[]) {
    this.graph.wasm.call((wasm) => {
      wasm.setSelectedLayers(this.nodeId, layerIds);
    });
  }

  toggleLayerSelection(layerId: LayerId) {
    const current = this.selectedLayersIds();
    const next = current.includes(layerId) ? current.filter((id) => id !== layerId) : [...current, layerId];
    this.setSelectedLayerIds(next);
  }

  selectLayerRange(clickedId: LayerId) {
    const current = this.selectedLayersIds();
    if (current.length === 0) {
      this.setSelectedLayerIds([clickedId]);
      return;
    }
    const { layers, layerOrder } = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;

    // Flatten the layer tree into the same top-to-bottom order the panel renders
    // (siblings reversed, each layer's children following it) so range selection
    // works across nesting levels rather than only at the top level.
    const flatten = (ids: LayerId[]): LayerId[] =>
      [...ids].reverse().flatMap((id) => {
        const layer = layers[id];
        return layer ? [id, ...flatten(layer.children)] : [];
      });
    const order = flatten(layerOrder);

    const anchor = current[0];
    const anchorIdx = order.indexOf(anchor);
    const clickIdx = order.indexOf(clickedId);
    if (anchorIdx !== -1 && clickIdx !== -1) {
      const from = Math.min(anchorIdx, clickIdx);
      const to = Math.max(anchorIdx, clickIdx);
      // Keep the already-selected layers and add the newly ranged ones on top,
      // preserving the current order so the anchor stays the first selection.
      const range = order.slice(from, to + 1);
      const combined = [...current, ...range.filter((id) => !current.includes(id))];
      this.setSelectedLayerIds(combined);
    }
  }

  /**
   * Selects all siblings of the current selection: the children of the shared
   * parent (or all root layers when the selection lives at the top level). Does
   * nothing when the selected layers don't all share the same parent. With an
   * empty selection, selects all root layers.
   */
  selectSiblingLayers() {
    const current = this.selectedLayersIds();
    const { layers, layerOrder } = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;

    const parentOf = new Map<LayerId, LayerId>();
    const recordParents = (ids: LayerId[], parent: LayerId | null) => {
      for (const id of ids) {
        const layer = layers[id];
        if (!layer) continue;
        if (parent !== null) parentOf.set(id, parent);
        recordParents(layer.children, id);
      }
    };
    recordParents(layerOrder, null);

    if (current.length === 0) {
      this.setSelectedLayerIds([...layerOrder]);
      return;
    }

    const parents = current.map((id) => parentOf.get(id) ?? null);
    const sharedParent = parents[0];
    if (!parents.every((parent) => parent === sharedParent)) {
      return;
    }

    const siblings = sharedParent === null ? layerOrder : layers[sharedParent].children;
    this.setSelectedLayerIds([...siblings]);
  }

  updateLayerName(layerIds: LayerId[], name: string) {
    this.applyLayerEdits(layerIds, (layer) => (layer.name === name ? undefined : { name }));
  }

  updateLayerLockedAspectRatio(layerIds: LayerId[], lockedAspectRatio: boolean) {
    this.applyLayerEdits(layerIds, (layer) =>
      layer.lockedAspectRatio === lockedAspectRatio ? undefined : { lockedAspectRatio },
    );
  }

  flipLayers(layerIds: LayerId[], orientation: Orientation) {
    this.graph.edit(() =>
      this.graph.wasmChange((wasm) => wasm.flipCompositorLayers(this.nodeId, layerIds, orientation)),
    );
  }

  /** Clockwise 90° in compositor view space (right-handed Z: negative increment). */
  rotateLayers90Clockwise(layerIds: LayerId[]) {
    this.graph.edit(() =>
      this.graph.wasmChange((wasm) => wasm.rotateCompositorLayers90Clockwise(this.nodeId, layerIds)),
    );
  }

  alignLayers(layerIds: LayerId[], alignment: Alignment) {
    this.graph.edit(() =>
      this.graph.wasmChange((wasm) => wasm.alignCompositorLayers(this.nodeId, layerIds, alignment)),
    );
  }

  /**
   * The layers a distribute of `layerIds` spaces: the given layers, or the
   * children of the layer when only one is given. Locked layers and layers that
   * show nothing (audio, empty groups) are left out.
   */
  distributionLayerIds(layerIds: LayerId[]): LayerId[] {
    return this.graph.wasm.call((wasm) => wasm.compositorDistributionLayerIds(this.nodeId, layerIds)) ?? [];
  }

  /** Spaces the layers {@link distributionLayerIds} picks evenly along `orientation`. */
  distributeLayers(layerIds: LayerId[], orientation: Orientation) {
    this.graph.edit(() =>
      this.graph.wasmChange((wasm) => wasm.distributeCompositorLayers(this.nodeId, layerIds, orientation)),
    );
  }

  addNodeInputHandle() {
    this.graph.edit(() => {
      return this.addNodeInputHandleInternal().undoEntry;
    });
  }

  addNodeInputHandleWithKey(): { key: string; undoEntry: UndoRedoEntry } {
    return this.addNodeInputHandleInternal();
  }

  private addNodeInputHandleInternal(): { key: string; undoEntry: UndoRedoEntry } {
    const [key] = this.allocateInputKeys(1);
    return { key, undoEntry: this.addInputHandleForKey(key) };
  }

  /**
   * Reserves `count` fresh compositor input keys. The next index comes from the highest existing
   * `layer_<n>` across both the engine input list and the client handle map, not from their length.
   * Counting collides once a delete leaves a gap: `layer_<count>` can equal a key still in use, and
   * the new input would then silently wire into content that is already there.
   */
  private allocateInputKeys(count: number): string[] {
    const nodeData = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId);
    const inputKeys = nodeData.data.input.map(([key]) => key);
    const handleInput = nodeData.handles.input;
    const handleKeys = Array.isArray(handleInput) ? handleInput : Object.keys(handleInput);
    const pattern = /^layer_(\d+)$/;
    let maxIndex = 0;
    for (const key of [...inputKeys, ...handleKeys]) {
      const match = key.match(pattern);
      if (match) {
        maxIndex = Math.max(maxIndex, parseInt(match[1], 10));
      }
    }
    return Array.from({ length: count }, (_, i) => `layer_${maxIndex + i + 1}`);
  }

  /** Opens one compositor input handle with the given key, in both the engine and the client handle map. */
  private addInputHandleForKey(key: string): UndoRedoEntry {
    const undoEntry = this.graph.wasmChange((wasm) => wasm.addInputHandle(this.nodeId, key));
    undoEntry.add(
      this.graph.addNodeInputHandle(this.nodeId, key, {
        description: '',
        label: key,
        format: 'uri',
        id: uuidv4(),
        order: Number(key.slice('layer_'.length)),
        required: false,
      }),
    );
    return undoEntry;
  }

  /**
   * Converts a point on the compositor canvas to a stage position, or undefined if the compositor
   * has no active camera (its editor is closed). `canvasX`/`canvasY` must already be in the
   * drawing-buffer pixel space that `canvas_position` uses: origin at the bottom-left, y up.
   */
  canvasPointToStage(canvasX: number, canvasY: number): { x: number; y: number } | undefined {
    return this.graph.wasm.call((wasm) => wasm.compositorCanvasPointToStage(this.nodeId, canvasX, canvasY));
  }

  /**
   * Adds each uploaded file to the compositor as a layer that owns the file
   * inline, in one undo step. Image, video, and audio are accepted; anything else
   * would mean an unexpected upload response and is left out rather than turned
   * into a broken layer. The engine op sets the timeline and duration from the
   * first timed clip when needed.
   */
  addMediaLayers(assets: MediaAsset[], placement: LayerPlacement, source: AddContentSource) {
    const files: FileKind[] = [];
    for (const asset of assets) {
      const file = validateFile(asset);
      if (file?.type === 'image' || file?.type === 'video' || file?.type === 'audio') {
        files.push(file);
      }
    }
    if (files.length === 0) {
      return;
    }

    this.graph.wasmEdit((wasm) => wasm.addMediaLayers(this.nodeId, files, placement));
    // Building the layer does not fetch its bytes, so without this the image
    // draws as an empty frame until the next full graph load.
    this.graph.loadResources();
    track('compositor_content_added', { source, count: files.length });
  }

  /** One undoable import, independent of the open editor and current selection. */
  importImages(
    images: Extract<FileKind, { type: 'image' }>[],
    groupName: string,
    sourceLayerId: LayerId,
    {
      wrapInGroup = true,
      hideSourceLayer = false,
      customBounds,
    }: { wrapInGroup?: boolean; hideSourceLayer?: boolean; customBounds?: CompositorAiBounds } = {},
  ) {
    if (this.graph.hasOngoingAction()) throw new CompositorAiError(CompositorAiErrorCode.ApplyFailed);
    let layerId!: LayerId;
    this.graph.edit(() => {
      const edit = this.graph.wasm.edit((wasm) => {
        const result = wasm.importCompositorImages(
          this.nodeId,
          images,
          sourceLayerId,
          groupName,
          wrapInGroup,
          customBounds,
        );
        layerId = result.layerId;
        return result.edit;
      });
      if (edit === undefined || layerId === undefined) throw new CompositorAiError(CompositorAiErrorCode.ApplyFailed);
      const undoEntry = UndoRedoEntry.fromWasmEdit(edit);
      if (hideSourceLayer && images.length > 0) {
        const sourceLayer = this.getLayer(sourceLayerId);
        if (!sourceLayer) throw new CompositorAiError(CompositorAiErrorCode.ApplyFailed);
        const visibilityEdit = this.graph.wasm.edit((wasm) =>
          wasm.updateLayer(this.nodeId, sourceLayerId, { ...sourceLayer, visible: false }),
        );
        if (visibilityEdit === undefined) throw new CompositorAiError(CompositorAiErrorCode.ApplyFailed);
        undoEntry.add(UndoRedoEntry.fromWasmEdit(visibilityEdit));
      }
      return undoEntry;
    });
    return layerId;
  }

  /**
   * Adds a layer showing content the compositor already holds, at the stage
   * center or a given point, in one undo step. Placing the same content twice
   * gives two layers on it.
   *
   * Nothing happens when the content follows a wire that is carrying nothing, or
   * that has come to carry another media type since the gesture started.
   */
  addLayerForResource(resourceId: ResourceId, placement: LayerPlacement) {
    this.graph.wasmEdit((wasm) => wasm.addLayerForResource(this.nodeId, resourceId, placement));
    // Building the layer does not fetch its bytes, so without this the new layer
    // draws as an empty frame until the next full graph load.
    this.graph.loadResources();
    // The same event duplicate and paste send, with the panel as another way of
    // showing one piece of content twice.
    track('compositor_content_reused', { source: 'assets_panel', count: 1 });
  }

  /**
   * Removes content the compositor holds along with every layer showing it, in
   * one undo step, so one undo brings the content and all its layers back. A
   * locked layer and one nested in a group go too, and their own children are
   * kept and moved into their place.
   *
   * Content that follows a wire is left alone: the engine refuses it, because
   * reconciliation would build a layer for it again.
   */
  removeResource(resourceId: ResourceId) {
    const before = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;
    if (before.resources[resourceId] === undefined) {
      return;
    }

    this.graph.wasmEdit((wasm) => wasm.removeResource(this.nodeId, resourceId));
    // Frees the pictures and videos the deleted layers were holding. Nothing
    // else does it on the way out, so without this they stay in memory until
    // some other change happens to load resources.
    this.graph.loadResources();

    // Read which layers survived after the edit rather than working out which
    // ones would go before it. The property panel on the right would otherwise
    // keep showing a layer that is no longer in the document, and a list made
    // before the confirmation dialog opened could be out of date by now.
    const after = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;
    this.setSelectedLayerIds(this.selectedLayersIds().filter((layerId) => after.layers[layerId] !== undefined));

    // The engine leaves content that follows a wire alone, so nothing was
    // deleted and there is nothing to report.
    if (before.resources[resourceId].content.type === 'value') {
      track('compositor_content_deleted', { count: 1 });
    }
  }

  /**
   * Removes the given layers in one undo step, top-level or nested in a group.
   * Added content (a layer that owns its file inline) and value shapes/text have
   * no wire, so they are the layers this removes. A layer fed by a connected
   * node is left alone. A group goes with everything inside it, except wired
   * layers, which the engine moves out to the top level and keeps. Lock does not
   * stop a delete. The engine detaches a nested layer from its group.
   *
   * This is the single delete path for both the Delete key and the menu, so the
   * eligibility rule and the analytics event are the same however you delete.
   *
   * `assetsPanelEnabled` is passed in rather than read here, because the flag has
   * to be the same one the menu was drawn from. Asking PostHog here instead
   * misses the `?featureFlagsOn=` override the React hook honours, which is how
   * the flag gets turned on in development, and the two answers disagreeing
   * makes the delete quietly do nothing.
   *
   * It is needed at all because without the panel there is nowhere to put a
   * deleted wired layer back from, so the engine is asked to keep it. That has to
   * reach the engine rather than only filter the ids here: a group carries no
   * wire of its own, so filtering what the user selected would let a group
   * through and lose the wired media inside it.
   */
  deleteLayers(layerIds: LayerId[], assetsPanelEnabled: boolean) {
    const compositor = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;
    const { layers } = compositor;
    const removable = layerIds.filter(
      (layerId) => layers[layerId] && isDeletableLayer(compositor, layers[layerId], assetsPanelEnabled),
    );
    if (removable.length === 0) {
      return;
    }

    this.removeLayers(removable, !assetsPanelEnabled);
    // Frees the pictures and videos the deleted layers were holding. Nothing
    // else does it on the way out, so without this they stay in memory until
    // some other change happens to load resources.
    this.graph.loadResources();
    const removed = new Set(removable);
    this.graph.wasm.call((wasm) =>
      wasm.setSelectedLayers(
        this.nodeId,
        layerIds.filter((id) => !removed.has(id)),
      ),
    );

    const deletedContentCount = countOwnedMedia(compositor, removable, assetsPanelEnabled);
    if (deletedContentCount > 0) {
      track('compositor_content_deleted', { count: deletedContentCount });
    }
  }

  /**
   * Duplicates the layers that can be duplicated (anything that owns its
   * content, including a whole group, plus media fed by a node once the panel is
   * on) among the given layers in one undo step, placing each copy just off its
   * original and selecting the copies. Text fed by a node, or a group with
   * nothing clonable, is left out.
   *
   * `allowClips` false also leaves out a video or audio, and any group holding
   * one, so the ids have to be filtered here rather than left to the engine: the
   * engine happily clones a clip that sits inside a group.
   *
   * The panel flag goes to the engine instead of being applied here, because a
   * group carries no wire of its own. Filtering ids would let a group holding one
   * owned layer through, and its wired children would be cloned with the flag
   * off. Same reason `deleteLayers` passes its flag down.
   */
  duplicateLayers(layerIds: LayerId[], allowClips: boolean, assetsPanelEnabled: boolean) {
    const compositor = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;
    const { layers } = compositor;
    const reusable = layerIds.filter(
      (id) => layers[id] && canDuplicateLayer(compositor, layers[id], allowClips, assetsPanelEnabled),
    );
    const count = reusable.length;
    if (count === 0) {
      return;
    }
    this.graph.wasmEdit((wasm) => wasm.duplicateLayers(this.nodeId, reusable, assetsPanelEnabled));
    // A copy carries the file url but building it does not fetch the bytes, so
    // without this a duplicated image/video draws blank until the next load.
    this.graph.loadResources();
    track('compositor_content_reused', { source: 'duplicate', count });
  }

  /**
   * Copies the layers that can be copied among `layerIds`, shared across every
   * open compositor for the session, and returns the HTML the caller must put on
   * the clipboard. Returns undefined when the selection holds nothing copyable,
   * leaving any earlier copy in place. The caller does the writing because only
   * a copy event is allowed to set the clipboard.
   *
   * Anything fed by a node is left out, media included, where duplicate takes
   * the media. See `canCopyLayer`.
   */
  copyLayers(layerIds: LayerId[], allowClips: boolean): string | undefined {
    const compositor = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;
    const { layers } = compositor;
    const reusable = layerIds.filter((id) => layers[id] && canCopyLayer(compositor, layers[id], allowClips));
    const payload = this.graph.wasm.call((wasm) => wasm.copyLayersPayload(this.nodeId, reusable)) ?? undefined;
    if (!payload) {
      return undefined;
    }
    const count = reusable.length;
    const token = uuidv4();
    CompositorView.copiedLayers = { token, payload, count };
    return `${CLIPBOARD_TOKEN_START}${token}${CLIPBOARD_TOKEN_END}`;
  }

  /**
   * Pastes the copied layers only if the clipboard still carries the token from
   * that copy. Any copy, in any program, replaces the whole clipboard, so a
   * token that is still there means nothing newer has been copied and the
   * layers are safe to paste. A missing token means the caller should look at
   * what is on the clipboard instead.
   */
  pasteLayersFromClipboard(clipboardHtml: string): boolean {
    const copied = CompositorView.copiedLayers;
    if (!copied || !clipboardHtml.includes(`${CLIPBOARD_TOKEN_START}${copied.token}${CLIPBOARD_TOKEN_END}`)) {
      return false;
    }
    return this.pasteLayers();
  }

  /**
   * Pastes the copied layers into this compositor at the top level in one undo
   * step, selecting them, and records the reuse. Returns whether a paste
   * happened. The copy may have been made in a different compositor, so a
   * pasted video or audio starts at the beginning of this timeline rather than
   * where it sat in the one it came from.
   */
  pasteLayers(): boolean {
    const copied = CompositorView.copiedLayers;
    if (!copied) {
      return false;
    }
    this.graph.wasmEdit((wasm) => wasm.pasteLayersPayload(this.nodeId, copied.payload));
    this.graph.loadResources();
    track('compositor_content_reused', { source: 'paste', count: copied.count });
    return true;
  }

  enableNodeEditor() {
    this.graph.wasm.call((wasm) => wasm.enableNodeEditor(this.nodeId));
    this.setToolMode('selection');
    this.zoomToFit();
  }

  disableNodeEditor() {
    this.graph.wasm.call((wasm) => wasm.disableNodeEditor());
  }

  renderResult() {
    this.graph.updateNodeOutputs(this.nodeId);
  }

  cancelOngoingAction() {
    this.graph.cancelOngoingAction();
    this.graph.loadFontResources();
  }

  undo() {
    this.graph.undo();
  }

  redo() {
    this.graph.redo();
  }

  clearUndoRedoHistory() {
    this.graph.clearUndoRedoHistory();
  }

  setUndoBarrier() {
    this.graph.setUndoBarrier();
  }

  disableUndoBarrier() {
    this.graph.disableUndoBarrier();
  }

  /**
   * True when the compositor holds video or audio, so the timeline toggle must stay on: turning it
   * off would freeze a video to one frame and drop audio entirely. Detects both the wired form (an
   * input handle typed video or audio) and the owned form (a layer holding its own file inline).
   */
  hasTimelineLockingContent(): boolean {
    const compositor = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;
    const { input, layers } = compositor;
    const wiredVideoOrAudio =
      input?.some(([_, data]) => {
        if (!data) return false;
        const type = this.graph.nodeOutputType(data.nodeId, data.outputId);
        return type === HandleType.Video || type === HandleType.Audio;
      }) ?? false;
    if (wiredVideoOrAudio) {
      return true;
    }
    // An uploaded video or sound has no wire to detect, so scan the layers too.
    return Object.values(layers).some((layer) => {
      const resource = resolveLayerResource(compositor, layer);
      return (resource?.type === 'video' || resource?.type === 'audio') && resource.content.type === 'value';
    });
  }
}
